/**
 * Hooks React Query pour le générateur paramétrique d'exercices (Blueprint).
 *
 * ⚠️ Modèle « UN exercice paramétré » (migration 0229, correction de cadrage de
 * 0221). Un Blueprint ne fabrique PAS N exercices : il en matérialise UN SEUL
 * (`exercise_blueprints.exercise_id`, `exercises.is_parametric = true`) rendu
 * avec une instance CANONIQUE, puis alimente une BANQUE de tirages
 * (`blueprint_instances`). `start_attempt` choisit une instance et la fige sur
 * la tentative ; `get_exercise_v2_for_student` applique son calque. Ce sont
 * donc bien les tentatives qui déterminent les valeurs des paramètres — et,
 * par ricochet, tout le solve path / corrigé qui en dérive.
 *
 * - `useBuildBlueprint` : edge fn `blueprint-build` (1 appel IA, structuration).
 * - `useBlueprints` / `useBlueprint` : lecture PostgREST (RLS auteur-ou-admin).
 * - `useMaterializeParametricExercise` : crée (ou reconstruit) l'UNIQUE exercice
 *   et mémorise le `block_map` depuis le `client_id_map` de l'edge fn.
 * - `useFillInstanceBank` : AUCUN appel IA — exécute le moteur pur N fois et
 *   insère N lignes `blueprint_instances`.
 * - `useBlueprintInstances` / `useSetInstanceActive` : revue de la banque.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import {
  generateInstance,
  instanceToPayload,
  blueprintBlockMapFromClientIdMap,
  validateBlueprintSpec,
  MATH_FUNCTION_IDS,
  BlueprintSpecSchema,
  BlueprintBlockMapSchema,
  type BlueprintSpec,
  type BlueprintBlockMap,
  type BlueprintInstancePayload,
} from '@jeraisonne/db/blueprint';
import { applyBlockOps } from '@/lib/exercise-v2/generation-client';
import { generatedInstanceToBlockOps } from './generated-instance-to-blocks';
import { toExerciseVisualBlock } from './generated-visual-to-block';
import type { BlockMutationOp, ExerciseV2Payload } from '@/lib/exercise-v2/types';

// exercise_blueprints est postérieure à types.generated.ts (stale volontaire,
// cf. CLAUDE.md) → client générique + cast `as unknown as` du résultat.
const db = supabase as unknown as SupabaseClient;

export type BlueprintStatus = 'draft' | 'active' | 'archived';
export type BlueprintSourceKind = 'teacher' | 'student';

export interface ExerciseBlueprintRow {
  id: string;
  title: string;
  level: string;
  chapter_id: string | null;
  source_statement: string;
  source_corrige: string;
  source_kind: BlueprintSourceKind;
  spec: BlueprintSpec;
  version: number;
  status: BlueprintStatus;
  /** L'UNIQUE exercice paramétrique de ce Blueprint (null tant qu'il n'est pas matérialisé). */
  exercise_id: string | null;
  /** Carte {élément de spec → uuid de bloc} — indispensable au calque des instances. */
  block_map: BlueprintBlockMap | Record<string, never>;
  canonical_seed: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export class BlueprintBuildError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'BlueprintBuildError';
  }
}

export interface BuildBlueprintInput {
  title: string;
  level: string;
  chapter_id: string | null;
  statement: string;
  corrige: string;
  source_kind: BlueprintSourceKind;
  /** Visuel (table/function_graph) détecté par vision au scan de l'énoncé — cf. extract-visual-blocks. */
  source_visual?: unknown;
}

async function invokeBuild(body: BuildBlueprintInput): Promise<{ blueprint_id: string }> {
  const { data, error } = await supabase.functions.invoke<{ ok: boolean; blueprint_id: string }>(
    'blueprint-build',
    { body },
  );
  if (error) {
    let message = error.message;
    let code: string | undefined;
    const ctx = (error as Error & { context?: Response }).context;
    if (ctx && typeof ctx.json === 'function') {
      try {
        const parsed = (await ctx.json()) as { error?: string; message?: string };
        code = parsed.error;
        message = parsed.message ?? parsed.error ?? message;
      } catch {
        /* garde le message générique */
      }
    }
    throw new BlueprintBuildError(message, code);
  }
  if (!data) throw new BlueprintBuildError('empty_response');
  return data;
}

export function useBuildBlueprint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: invokeBuild,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blueprints'] });
    },
  });
}

export function useBlueprints() {
  return useQuery({
    queryKey: ['blueprints'],
    queryFn: async (): Promise<ExerciseBlueprintRow[]> => {
      const { data, error } = await db
        .from('exercise_blueprints')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as ExerciseBlueprintRow[];
    },
  });
}

export const blueprintQueryKey = (id: string) => ['blueprint', id] as const;

export function useBlueprint(blueprintId: string | null | undefined) {
  return useQuery({
    queryKey: blueprintQueryKey(blueprintId ?? ''),
    enabled: !!blueprintId,
    queryFn: async (): Promise<ExerciseBlueprintRow> => {
      const { data, error } = await db
        .from('exercise_blueprints')
        .select('*')
        .eq('id', blueprintId as string)
        .single();
      if (error) throw new Error(error.message);
      return data as unknown as ExerciseBlueprintRow;
    },
  });
}

export function useUpdateBlueprintStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: BlueprintStatus }) => {
      const { error } = await db.from('exercise_blueprints').update({ status }).eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: blueprintQueryKey(vars.id) });
      qc.invalidateQueries({ queryKey: ['blueprints'] });
    },
  });
}

/**
 * Corrige la spec d'un Blueprint après extraction LLM (le tuteur relit la
 * segmentation et ajuste ce qui est faux : paramètre mal borné, fonction
 * incorrecte, contrainte manquante, template qui fuite un résultat…).
 * Revalide avec les mêmes règles que `blueprint-build` (schéma Zod + fermeture
 * anti-hallucination `validateBlueprintSpec`) AVANT d'écrire — jamais de spec
 * invalide persistée. Incrémente `version` (cf. commentaire migration 0221) :
 * les instances de la banque deviennent stale (filtrées par `blueprint_version`
 * dans la page de revue) et les blocs matérialisés doivent être reconstruits
 * manuellement (bouton déjà existant "Reconstruire les blocs").
 */
export function useUpdateBlueprintSpec() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      blueprint,
      spec,
    }: {
      blueprint: ExerciseBlueprintRow;
      spec: BlueprintSpec;
    }): Promise<{ version: number }> => {
      const parsed = BlueprintSpecSchema.parse(spec);
      validateBlueprintSpec(parsed, { candidateFunctionIds: MATH_FUNCTION_IDS });
      const version = blueprint.version + 1;
      const { error } = await db
        .from('exercise_blueprints')
        .update({ spec: parsed, version })
        .eq('id', blueprint.id);
      if (error) throw new Error(error.message);
      return { version };
    },
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: blueprintQueryKey(vars.blueprint.id) });
      qc.invalidateQueries({ queryKey: blueprintInstancesQueryKey(vars.blueprint.id) });
      qc.invalidateQueries({ queryKey: ['blueprints'] });
    },
  });
}

// ── Matérialisation de l'UNIQUE exercice paramétrique ───────────────────────

const CANONICAL_SEED = 'canonical';

/** Supprime les blocs existants, enfants d'abord (le trigger de topologie refuse un parent orphelin). */
function buildWipeOps(blocks: { id: string; block_type: string }[]): BlockMutationOp[] {
  const rank: Record<string, number> = {
    distractor: 0,
    correction: 1,
    statement_question: 2,
    statement: 3,
  };
  return [...blocks]
    .sort((a, b) => (rank[a.block_type] ?? 9) - (rank[b.block_type] ?? 9))
    .map((b) => ({ op: 'delete', id: b.id }) as BlockMutationOp);
}

export interface MaterializeResult {
  exerciseId: string;
  blockMap: BlueprintBlockMap;
  rebuilt: boolean;
}

/**
 * Crée l'exercice paramétrique du Blueprint (ou reconstruit ses blocs si la
 * spec a changé), à partir de l'instance CANONIQUE : c'est ce rendu figé que
 * voient toutes les surfaces sans tentative (Live School, défis sociaux,
 * showcase, recherche, éditeur). Mémorise ensuite le `block_map`.
 */
export function useMaterializeParametricExercise() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({
      blueprint,
    }: {
      blueprint: ExerciseBlueprintRow;
    }): Promise<MaterializeResult> => {
      if (!blueprint.chapter_id) {
        throw new Error("Le Blueprint doit être rattaché à un chapitre avant d'être matérialisé.");
      }
      const spec = BlueprintSpecSchema.parse(blueprint.spec);
      const canonical = generateInstance(spec, {
        seed: blueprint.canonical_seed || CANONICAL_SEED,
      });

      let exerciseId = blueprint.exercise_id;
      const rebuilt = exerciseId !== null;

      if (!exerciseId) {
        const { data: exo, error: exoError } = await db
          .from('exercises')
          .insert({
            chapter_id: blueprint.chapter_id,
            created_by: user?.id ?? null,
            title: blueprint.title,
            statement_md: canonical.statement_md,
            difficulty: 'medium',
            estimated_minutes: 5,
            content_version: 'v2',
            status: 'draft',
            is_parametric: true,
            generator_blueprint_id: blueprint.id,
            generator_version: blueprint.version,
            generator_seed: blueprint.canonical_seed || CANONICAL_SEED,
          })
          .select('id')
          .single();
        if (exoError || !exo) throw new Error(exoError?.message ?? 'Échec création exercice');
        exerciseId = (exo as { id: string }).id;
      } else {
        // Reconstruction : on repart d'un arbre vide pour que la topologie
        // corresponde exactement à la spec courante (le calque d'une instance
        // est indexé par uuid de bloc — un décalage rendrait la banque invalide).
        const payload = await invokeGetForEdit(exerciseId);
        const wipe = buildWipeOps(payload.blocks ?? []);
        if (wipe.length > 0) await applyBlockOps(exerciseId, wipe);
        const { error: updErr } = await db
          .from('exercises')
          .update({
            title: blueprint.title,
            statement_md: canonical.statement_md,
            is_parametric: true,
            generator_version: blueprint.version,
          })
          .eq('id', exerciseId);
        if (updErr) throw new Error(updErr.message);
      }

      const clientIdMap = await applyBlockOps(exerciseId, generatedInstanceToBlockOps(canonical));
      const blockMap = blueprintBlockMapFromClientIdMap(canonical, clientIdMap);
      BlueprintBlockMapSchema.parse(blockMap);

      const { error: bpErr } = await db
        .from('exercise_blueprints')
        .update({
          exercise_id: exerciseId,
          block_map: blockMap,
          canonical_seed: blueprint.canonical_seed || CANONICAL_SEED,
        })
        .eq('id', blueprint.id);
      if (bpErr) throw new Error(bpErr.message);

      // Les instances de l'ancienne topologie ne peuvent plus être calquées.
      if (rebuilt) {
        await db
          .from('blueprint_instances')
          .update({ is_active: false })
          .eq('blueprint_id', blueprint.id);
      }

      return { exerciseId, blockMap, rebuilt };
    },
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: blueprintQueryKey(vars.blueprint.id) });
      qc.invalidateQueries({ queryKey: blueprintInstancesQueryKey(vars.blueprint.id) });
      qc.invalidateQueries({ queryKey: ['blueprints'] });
      qc.invalidateQueries({ queryKey: ['tutor-exercises'] });
    },
  });
}

async function invokeGetForEdit(exerciseId: string): Promise<ExerciseV2Payload> {
  const { data, error } = await supabase.functions.invoke<ExerciseV2Payload>(
    'exercise-get-for-edit',
    {
      body: { exercise_id: exerciseId },
    },
  );
  if (error) throw error;
  if (!data) throw new Error('empty_response');
  return data;
}

// ── Banque d'instances ──────────────────────────────────────────────────────

export interface BlueprintInstanceRow {
  id: string;
  blueprint_id: string;
  blueprint_version: number;
  seed: string;
  payload: BlueprintInstancePayload;
  is_active: boolean;
  serve_count: number;
  created_at: string;
}

export const blueprintInstancesQueryKey = (blueprintId: string) =>
  ['blueprint-instances', blueprintId] as const;

export function useBlueprintInstances(blueprintId: string | null | undefined) {
  return useQuery({
    queryKey: blueprintInstancesQueryKey(blueprintId ?? ''),
    enabled: !!blueprintId,
    queryFn: async (): Promise<BlueprintInstanceRow[]> => {
      const { data, error } = await db
        .from('blueprint_instances')
        .select('*')
        .eq('blueprint_id', blueprintId as string)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as BlueprintInstanceRow[];
    },
  });
}

/**
 * Alimente la banque : N tirages du moteur pur (aucun appel IA), insérés comme
 * calques. La topologie de l'exercice ne bouge pas — `instanceToPayload` lève
 * si la spec a dérivé depuis la matérialisation (il faut alors reconstruire).
 */
export function useFillInstanceBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      blueprint,
      count,
    }: {
      blueprint: ExerciseBlueprintRow;
      count: number;
    }): Promise<{ inserted: number }> => {
      if (!blueprint.exercise_id) {
        throw new Error("Matérialise d'abord l'exercice paramétrique du Blueprint.");
      }
      const spec = BlueprintSpecSchema.parse(blueprint.spec);
      const blockMap = BlueprintBlockMapSchema.parse(blueprint.block_map);

      const rows = Array.from({ length: count }, () => {
        const seed = crypto.randomUUID();
        const instance = generateInstance(spec, { seed });
        return {
          blueprint_id: blueprint.id,
          blueprint_version: blueprint.version,
          seed,
          payload: instanceToPayload(instance, blockMap, { renderVisual: toExerciseVisualBlock }),
        };
      });

      const { error } = await db.from('blueprint_instances').insert(rows);
      if (error) throw new Error(error.message);
      return { inserted: rows.length };
    },
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: blueprintInstancesQueryKey(vars.blueprint.id) });
    },
  });
}

export function useSetInstanceActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      instanceId,
      isActive,
    }: {
      blueprintId: string;
      instanceId: string;
      isActive: boolean;
    }) => {
      const { error } = await db
        .from('blueprint_instances')
        .update({ is_active: isActive })
        .eq('id', instanceId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: blueprintInstancesQueryKey(vars.blueprintId) });
    },
  });
}
